import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { User, LogOut, Calendar, Home, Package, Shield, Building2, Store, Menu, ShoppingCart, ChevronDown, Plus, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';

const Navbar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartCount } = useCart();
  const { businesses, activeBusiness, switchBusiness, hasMultipleBusinesses } = useAuth();
  const cartCount = getCartCount();
  const isPlatformAdmin = user?.is_platform_admin;
  const accountType = user?.account_type || 'user';
  const hasBusiness = user?.business?.business_id || businesses?.length > 0;

  const handleVendorsClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on landing page, just scroll
      document.getElementById('vendors')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to landing page, then scroll
      navigate('/');
      setTimeout(() => {
        document.getElementById('vendors')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSwitchBusiness = async (businessId) => {
    await switchBusiness(businessId);
  };

  return (
    <nav className="border-b border-gray-800 bg-gray-900/95 backdrop-blur-md sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="/logo.png" 
              alt="Gumba Logo" 
              className="h-10 object-contain group-hover:scale-105 transition-transform"
            />
            <span className="text-xl font-bold text-yellow-500 hidden sm:block">
              Gumba
            </span>
          </Link>

          <div className="flex items-center space-x-4">
            {user ? (
              <>
                {/* Business Selector - Only show if user has multiple businesses */}
                {businesses && businesses.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="hidden md:flex items-center gap-2 border-gray-600 text-gray-300 hover:text-white hover:bg-gray-800 max-w-[200px]"
                        data-testid="business-selector"
                      >
                        <Building2 className="h-4 w-4 flex-shrink-0 text-yellow-500" />
                        <span className="truncate">
                          {activeBusiness?.business_name || businesses[0]?.business_name || 'Select Business'}
                        </span>
                        <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64 bg-gray-800 border-gray-700">
                      <div className="px-3 py-2 border-b border-gray-700">
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Your Businesses</p>
                      </div>
                      {businesses.map((biz) => (
                        <DropdownMenuItem 
                          key={biz.business_id}
                          data-testid={`business-option-${biz.business_id}`}
                          onClick={() => handleSwitchBusiness(biz.business_id)}
                          className="text-gray-300 hover:text-white hover:bg-gray-700 cursor-pointer"
                        >
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4" />
                              <div>
                                <p className="font-medium">{biz.business_name}</p>
                                <p className="text-xs text-gray-500">
                                  {biz.verification_status === 'verified' ? (
                                    <span className="text-green-400">Verified</span>
                                  ) : (
                                    <span className="text-yellow-400">Pending</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {activeBusiness?.business_id === biz.business_id && (
                              <Check className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator className="bg-gray-700" />
                      <DropdownMenuItem 
                        onClick={() => navigate('/become-vendor')}
                        data-testid="add-another-business-btn"
                        className="text-yellow-500 hover:text-yellow-400 hover:bg-gray-700 cursor-pointer"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Another Business
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-2">
                  <Button 
                    variant="ghost" 
                    className="text-gray-300 hover:text-white hover:bg-gray-800"
                    onClick={handleVendorsClick}
                    data-testid="nav-vendors-btn"
                  >
                    <Store className="h-4 w-4 mr-2" />
                    Vendors
                  </Button>
                  <Link to="/browse">
                    <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-gray-800">
                      PA Systems
                    </Button>
                  </Link>
                  <Link to="/equipment">
                    <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-gray-800">
                      Equipment
                    </Button>
                  </Link>
                  <Link to="/my-bookings">
                    <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-gray-800">
                      <Calendar className="h-4 w-4 mr-2" />
                      Bookings
                    </Button>
                  </Link>
                  {isPlatformAdmin && (
                    <Link to="/admin">
                      <Button variant="ghost" className="text-yellow-500 hover:text-yellow-400 hover:bg-gray-800">
                        <Shield className="h-4 w-4 mr-2" />
                        Admin
                      </Button>
                    </Link>
                  )}
                  
                  {/* Cart Icon */}
                  <Button 
                    variant="ghost" 
                    className="text-gray-300 hover:text-white hover:bg-gray-800 relative"
                    onClick={() => navigate('/cart')}
                    data-testid="nav-cart-btn"
                  >
                    <ShoppingCart className="h-5 w-5" />
                    {cartCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-yellow-500 text-black text-xs font-bold">
                        {cartCount}
                      </Badge>
                    )}
                  </Button>
                </div>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full border border-gray-700 hover:border-yellow-500">
                      <Avatar>
                        <AvatarImage src={user?.picture} alt={user?.name || 'User'} />
                        <AvatarFallback className="bg-yellow-500 text-black font-semibold">
                          {user?.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64 bg-gray-800 border-gray-700">
                    <div className="flex items-center justify-start gap-2 p-3">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium text-white">{user?.name || 'User'}</p>
                        <p className="text-xs text-gray-400">{user?.email || ''}</p>
                        <div className="flex gap-1 mt-1">
                          <Badge className="text-xs bg-gray-700 text-gray-300">
                            {accountType === 'both' ? 'User & Business' : accountType}
                          </Badge>
                          {isPlatformAdmin && (
                            <Badge className="text-xs bg-yellow-500 text-black">Admin</Badge>
                          )}
                        </div>
                        {businesses && businesses.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {businesses.length} business{businesses.length > 1 ? 'es' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                    <DropdownMenuSeparator className="bg-gray-700" />
                    
                    {/* Mobile Navigation Items */}
                    <div className="md:hidden">
                      <DropdownMenuItem onClick={handleVendorsClick} className="text-gray-300 hover:text-white hover:bg-gray-700">
                        <Store className="mr-2 h-4 w-4" />
                        Browse Vendors
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/browse')} className="text-gray-300 hover:text-white hover:bg-gray-700">
                        <Home className="mr-2 h-4 w-4" />
                        PA Systems
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/equipment')} className="text-gray-300 hover:text-white hover:bg-gray-700">
                        <Package className="mr-2 h-4 w-4" />
                        Equipment
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/my-bookings')} className="text-gray-300 hover:text-white hover:bg-gray-700">
                        <Calendar className="mr-2 h-4 w-4" />
                        My Bookings
                      </DropdownMenuItem>
                      {isPlatformAdmin && (
                        <DropdownMenuItem onClick={() => navigate('/admin')} className="text-yellow-500 hover:text-yellow-400 hover:bg-gray-700">
                          <Shield className="mr-2 h-4 w-4" />
                          Admin Dashboard
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-gray-700" />
                    </div>

                    {/* Business Links - Show submenu if multiple businesses */}
                    {businesses && businesses.length > 1 ? (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="text-gray-300 hover:text-white hover:bg-gray-700">
                          <Building2 className="mr-2 h-4 w-4" />
                          Manage Businesses
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="bg-gray-800 border-gray-700">
                          {businesses.map((biz) => (
                            <DropdownMenuItem 
                              key={biz.business_id}
                              onClick={() => {
                                handleSwitchBusiness(biz.business_id);
                                navigate('/business/dashboard');
                              }}
                              className="text-gray-300 hover:text-white hover:bg-gray-700"
                            >
                              <div className="flex items-center justify-between w-full">
                                <span className="truncate">{biz.business_name}</span>
                                {activeBusiness?.business_id === biz.business_id && (
                                  <Check className="h-3 w-3 text-green-500 ml-2" />
                                )}
                              </div>
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator className="bg-gray-700" />
                          <DropdownMenuItem 
                            onClick={() => navigate('/become-vendor')}
                            className="text-yellow-500 hover:text-yellow-400 hover:bg-gray-700"
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Add Business
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ) : (accountType === 'business' || accountType === 'both') && hasBusiness ? (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/business/dashboard')} className="text-gray-300 hover:text-white hover:bg-gray-700">
                          <Building2 className="mr-2 h-4 w-4" />
                          Business Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/become-vendor')} className="text-yellow-500 hover:text-yellow-400 hover:bg-gray-700">
                          <Plus className="mr-2 h-4 w-4" />
                          Add Another Business
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-gray-700" />
                      </>
                    ) : null}

                    {/* Become a Vendor - for customers/users without business */}
                    {(accountType === 'customer' || accountType === 'user') && !hasBusiness && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/become-vendor')} className="text-yellow-500 hover:text-yellow-400 hover:bg-gray-700">
                          <Store className="mr-2 h-4 w-4" />
                          Become a Vendor
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-gray-700" />
                      </>
                    )}

                    {/* Complete Business Setup - for business type without profile */}
                    {(accountType === 'business' || accountType === 'both') && !hasBusiness && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/business/register')} className="text-yellow-500 hover:text-yellow-400 hover:bg-gray-700">
                          <Building2 className="mr-2 h-4 w-4" />
                          Complete Business Setup
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-gray-700" />
                      </>
                    )}

                    <DropdownMenuItem onClick={onLogout} className="text-red-400 hover:text-red-300 hover:bg-gray-700">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-gray-300 hover:text-white hover:bg-gray-800">
                    Sign In
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold">
                    <User className="h-4 w-4 mr-2" />
                    Register
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
